import { useState } from 'react';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { OrderComponent } from '@/hooks/useOrderComponents';
import { CHECKLIST_SECTIONS, normaliseProgress } from './AssemblyChecklist';

interface PrintBuildSheetProps {
  order: any;
  components?: OrderComponent[];
  canViewCosts?: boolean;
}

function fmt(n: number) {
  return '€' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtDate(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Auto-remarks based on build rules ──────────────────────────────────────
function deriveRemarks(
  buildSku: string,
  components: OrderComponent[],
  specsMap: Record<string, Record<string, any>>,
): string[] {
  const remarks: string[] = [];

  // 1. No OS or -NL suffix → activation sheet
  const hasOS = components.some(c =>
    (c.type || '').toUpperCase() === 'OS' ||
    (c.type || '').toLowerCase() === 'operating system'
  );
  const isNL = buildSku?.toUpperCase().endsWith('-NL');
  if (!hasOS || isNL) {
    remarks.push('Add Activation sheet in AOI Box');
  }

  // 2. Case has USB-C ports but MB has no USB-C header → inop sheet
  const mbSku = components.find(c => ['MB', 'MOBO', 'MOTHERBOARD'].includes((c.type || '').toUpperCase()))?.sku;
  const casSku = components.find(c => ['CAS', 'CASE', 'CHASSIS'].includes((c.type || '').toUpperCase()))?.sku;
  const mbSpecs = mbSku ? specsMap[mbSku] : null;
  const casSpecs = casSku ? specsMap[casSku] : null;
  if (casSpecs && (casSpecs.usbc_ports ?? 0) > 0 && mbSpecs && !mbSpecs.usbc_header) {
    remarks.push('Include USB-C Inop sheet');
  }

  return remarks;
}

function buildPrintHTML(
  order: any,
  components: OrderComponent[],
  descMap: Record<string, string>,
  toNumber: string,
  canViewCosts: boolean,
  reportNumber: number,
  remarks: string[],
): string {
  const unitCount = Math.max(order.quantity || 1, 1);
  const allUnits = normaliseProgress(order.checklist_progress, unitCount);
  // For print summary, use unit 1's progress (or aggregate)
  const progress: Record<string, boolean> = allUnits['1'] ?? {};
  const allItems = CHECKLIST_SECTIONS.flatMap(s => s.items);
  const doneCount = allItems.filter(i => progress[i.key]).length;
  const pct = allItems.length > 0 ? Math.round((doneCount / allItems.length) * 100) : 0;

  const totalCost = components.reduce((s, c) => s + c.line_total, 0);

  const componentRows = components.map(c => {
    const desc = descMap[c.sku] || '—';
    const costCols = canViewCosts
      ? `<td style="padding:6px 8px;text-align:right;font-family:monospace;">${fmt(c.unit_cost)}</td>
         <td style="padding:6px 8px;text-align:right;font-family:monospace;font-weight:600;">${fmt(c.line_total)}</td>`
      : '';
    return `<tr style="border-bottom:1px solid #e5e7eb;">
      <td style="padding:6px 8px;font-size:11px;color:#6b7280;">${c.typeLabel}</td>
      <td style="padding:6px 8px;font-family:monospace;font-size:11px;">${c.sku}</td>
      <td style="padding:6px 8px;font-size:12px;">${desc}</td>
      <td style="padding:6px 8px;text-align:center;">${c.qty}</td>
      ${costCols}
    </tr>`;
  }).join('');

  const costHeaders = canViewCosts
    ? `<th style="padding:8px;text-align:right;background:#f3f4f6;border-bottom:2px solid #d1d5db;">Unit Cost</th>
       <th style="padding:8px;text-align:right;background:#f3f4f6;border-bottom:2px solid #d1d5db;">Line Total</th>`
    : '';

  const costFooter = canViewCosts
    ? `<tfoot>
        <tr style="background:#f9fafb;">
          <td colspan="${canViewCosts ? 4 : 4}" style="padding:8px;text-align:right;font-weight:600;">Total BOM Cost:</td>
          <td style="padding:8px;text-align:right;font-family:monospace;font-weight:700;">${fmt(totalCost)}</td>
        </tr>
      </tfoot>`
    : '';

  const renderUnitChecklist = (unitProg: Record<string, boolean>) => CHECKLIST_SECTIONS.map(section => {
    const sectionDone = section.items.filter(i => unitProg[i.key]).length;
    const sectionComplete = sectionDone === section.items.length;
    const headerBg = sectionComplete ? '#dcfce7' : '#f3f4f6';
    const headerColor = sectionComplete ? '#166534' : '#374151';

    const items = section.items.map(item => {
      const checked = !!unitProg[item.key];
      const symbol = checked ? '☑' : '☐';
      const textStyle = checked
        ? 'color:#6b7280;text-decoration:line-through;'
        : 'color:#111827;';
      return `<div style="display:flex;align-items:center;gap:8px;padding:4px 12px;border-bottom:1px solid #f3f4f6;">
        <span style="font-size:16px;line-height:1;${checked ? 'color:#16a34a;' : 'color:#9ca3af;'}">${symbol}</span>
        <span style="font-size:12px;${textStyle}">${item.label}</span>
      </div>`;
    }).join('');

    return `<div style="border:1px solid ${sectionComplete ? '#86efac' : '#e5e7eb'};border-radius:8px;overflow:hidden;margin-bottom:10px;">
      <div style="background:${headerBg};padding:6px 12px;display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:13px;font-weight:700;color:${headerColor};">${section.label}</span>
        <span style="font-size:11px;color:${headerColor};">${sectionDone}/${section.items.length}</span>
      </div>
      ${items}
    </div>`;
  }).join('');

  const checklistSections = unitCount > 1
    ? Array.from({ length: unitCount }, (_, i) => {
        const unitKey = String(i + 1);
        const unitProg = allUnits[unitKey] ?? {};
        return `<h3 style="font-size:14px;font-weight:700;margin:16px 0 8px;">Unit ${unitKey}</h3>${renderUnitChecklist(unitProg)}`;
      }).join('')
    : renderUnitChecklist(progress);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Build Sheet — ${order.order_number}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; color: #111827; background: white; }
    @page { size: A4 portrait; margin: 15mm 15mm 15mm 15mm; }
    @media print {
      .page-break { page-break-before: always; }
    }
    table { width: 100%; border-collapse: collapse; }
  </style>
</head>
<body>

<!-- ═══════════════════════════════════════════════════════════════ PAGE 1 ══ -->
<div style="padding:0;">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #111827;padding-bottom:10px;margin-bottom:16px;">
    <div>
      <div style="font-size:22px;font-weight:900;letter-spacing:-0.5px;color:#111827;">AOI GAMING</div>
      <div style="font-size:11px;color:#6b7280;margin-top:2px;">Build Sheet / Work Order</div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:18px;font-weight:700;font-family:monospace;color:#111827;">${order.order_number}</div>
      <div style="font-size:14px;font-weight:700;color:#374151;margin-top:3px;">Report #${reportNumber}</div>
      <div style="font-size:11px;color:#6b7280;margin-top:2px;">Printed: ${fmtDate(new Date().toISOString())}</div>
    </div>
  </div>

  <!-- Order Info Grid -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;margin-bottom:16px;">
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Assembly Order</div>
      <div style="font-weight:700;font-family:monospace;font-size:14px;">${order.order_number}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Transfer Order (TO)</div>
      <div style="font-weight:700;font-family:monospace;font-size:14px;">${toNumber || '—'}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Build SKU</div>
      <div style="font-weight:600;font-family:monospace;">${order.build?.sku || '—'}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Description</div>
      <div style="font-weight:500;">${order.build?.description || '—'}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Type</div>
      <div>${order.type || order.notes || '—'}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Reservation</div>
      <div style="font-family:monospace;">${order.reservation?.reservation_code || '—'}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Technician</div>
      <div>${order.technician?.name || order.technician_name || '—'}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Quantity</div>
      <div style="font-weight:600;">${order.quantity || 1} unit${(order.quantity || 1) > 1 ? 's' : ''}</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Date Created</div>
      <div>${fmtDate(order.created_at)}</div>
    </div>
  </div>

  <!-- Components Table -->
  <div style="font-size:13px;font-weight:700;margin-bottom:8px;color:#374151;">Bill of Materials</div>
  ${components.length === 0
    ? `<div style="text-align:center;padding:24px;color:#9ca3af;border:1px dashed #d1d5db;border-radius:8px;">No components found for this order</div>`
    : `<div style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
      <table>
        <thead>
          <tr>
            <th style="padding:8px;text-align:left;background:#f3f4f6;border-bottom:2px solid #d1d5db;font-size:11px;">Type</th>
            <th style="padding:8px;text-align:left;background:#f3f4f6;border-bottom:2px solid #d1d5db;font-size:11px;">Part Number</th>
            <th style="padding:8px;text-align:left;background:#f3f4f6;border-bottom:2px solid #d1d5db;font-size:11px;">Description</th>
            <th style="padding:8px;text-align:center;background:#f3f4f6;border-bottom:2px solid #d1d5db;font-size:11px;">Qty</th>
            ${costHeaders}
          </tr>
        </thead>
        <tbody>${componentRows}</tbody>
        ${costFooter}
      </table>
    </div>`
  }

  ${remarks.length > 0 ? `
  <!-- Remarks -->
  <div style="margin-top:16px;border:2px solid #dc2626;border-radius:8px;padding:10px 14px;background:#fef2f2;">
    <div style="font-size:11px;color:#991b1b;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">⚠ Remarks</div>
    ${remarks.map(r => `<div style="font-size:13px;font-weight:600;color:#991b1b;padding:3px 0;">• ${r}</div>`).join('')}
  </div>` : ''}

  ${order.build?.comments ? `
  <!-- Build Comments -->
  <div style="margin-top:${remarks.length > 0 ? '12' : '16'}px;border:1px solid #bfdbfe;background:#eff6ff;border-radius:8px;padding:10px 14px;">
    <div style="font-size:11px;color:#1e40af;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Build Comments</div>
    <div style="font-size:12px;color:#111827;white-space:pre-wrap;">${order.build.comments}</div>
  </div>` : ''}

  <!-- Notes line -->
  <div style="margin-top:${(order.build?.comments || remarks.length > 0) ? '12' : '20'}px;border-top:1px solid #e5e7eb;padding-top:10px;">
    <div style="font-size:11px;color:#6b7280;margin-bottom:6px;font-weight:600;">NOTES</div>
    ${order.notes
      ? `<div style="font-size:12px;color:#111827;white-space:pre-wrap;padding:6px 0;border-bottom:1px solid #d1d5db;">${order.notes}</div>`
      : `<div style="height:40px;border-bottom:1px solid #d1d5db;"></div>`}
  </div>

</div>

<!-- ═══════════════════════════════════════════════════════════════ PAGE 2 ══ -->
<div class="page-break" style="padding:0;">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #111827;padding-bottom:10px;margin-bottom:16px;">
    <div>
      <div style="font-size:22px;font-weight:900;letter-spacing:-0.5px;color:#111827;">AOI GAMING</div>
      <div style="font-size:11px;color:#6b7280;margin-top:2px;">Build Checklist</div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:16px;font-weight:700;font-family:monospace;color:#111827;">${order.order_number}</div>
      <div style="font-size:13px;font-weight:700;color:#374151;margin-top:2px;">Report #${reportNumber}</div>
      <div style="font-size:11px;color:#6b7280;margin-top:1px;">${order.build?.sku || ''}</div>
    </div>
  </div>

  <!-- Overall Progress Bar -->
  <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;padding:10px 12px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;">
    <div style="font-size:12px;font-weight:600;color:#374151;white-space:nowrap;">Overall Progress</div>
    <div style="flex:1;height:10px;background:#e5e7eb;border-radius:999px;overflow:hidden;">
      <div style="height:100%;width:${pct}%;background:${pct === 100 ? '#16a34a' : '#3b82f6'};border-radius:999px;"></div>
    </div>
    <div style="font-size:12px;font-weight:600;color:${pct === 100 ? '#16a34a' : '#374151'};white-space:nowrap;">${doneCount}/${allItems.length} (${pct}%)</div>
  </div>

  <!-- Checklist Sections -->
  ${checklistSections}

  <!-- Sign-off -->
  <div style="margin-top:16px;border-top:1px solid #e5e7eb;padding-top:12px;display:grid;grid-template-columns:1fr 1fr;gap:16px;">
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Technician Sign-Off</div>
      <div style="border-bottom:1px solid #374151;height:28px;"></div>
      <div style="font-size:10px;color:#9ca3af;margin-top:2px;">Signature &amp; Date</div>
    </div>
    <div>
      <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Manager / QC Sign-Off</div>
      <div style="border-bottom:1px solid #374151;height:28px;"></div>
      <div style="font-size:10px;color:#9ca3af;margin-top:2px;">Signature &amp; Date</div>
    </div>
  </div>

</div>

</body>
</html>`;
}

async function fetchComponents(order: any): Promise<OrderComponent[]> {
  // 1. assembly_order_components
  if (order?.id) {
    const { data } = await supabase
      .from('assembly_order_components')
      .select('component_sku, component_type, component_qty, cost_unit_ex_vat')
      .eq('ao_id', order.id);
    if (data?.length) {
      return data.map((c: any) => ({
        sku: c.component_sku,
        type: c.component_type || '—',
        typeLabel: c.component_type || '—',
        description: '',
        qty: parseFloat(c.component_qty) || 1,
        unit_cost: parseFloat(c.cost_unit_ex_vat) || 0,
        line_total: (parseFloat(c.component_qty) || 1) * (parseFloat(c.cost_unit_ex_vat) || 0),
      }));
    }
  }
  // 2. reservation_components
  if (order?.reservation?.id) {
    const { data } = await supabase
      .from('reservation_components')
      .select('component_sku, component_type, quantity, unit_cost')
      .eq('reservation_id', order.reservation.id);
    if (data?.length) {
      return data.map((c: any) => ({
        sku: c.component_sku,
        type: c.component_type || '—',
        typeLabel: c.component_type || '—',
        description: '',
        qty: parseFloat(c.quantity) || 1,
        unit_cost: parseFloat(c.unit_cost) || 0,
        line_total: (parseFloat(c.quantity) || 1) * (parseFloat(c.unit_cost) || 0),
      }));
    }
  }
  // 3. build_components BOM
  if (order?.build?.id) {
    const { data } = await supabase
      .from('build_components')
      .select('component_sku, component_type, qty_per_build, cost_ex_vat')
      .eq('build_id', order.build.id)
      .order('component_type');
    if (data?.length) {
      return data.map((c: any) => ({
        sku: c.component_sku,
        type: c.component_type || '—',
        typeLabel: c.component_type || '—',
        description: '',
        qty: parseFloat(c.qty_per_build) || 1,
        unit_cost: parseFloat(c.cost_ex_vat) || 0,
        line_total: (parseFloat(c.qty_per_build) || 1) * (parseFloat(c.cost_ex_vat) || 0),
      }));
    }
  }
  return [];
}

export function PrintBuildSheet({ order, components, canViewCosts = false }: PrintBuildSheetProps) {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = async () => {
    if (!order) return;
    setIsPrinting(true);
    try {
      // Prompt for TO number if not set
      let toNumber: string = order.to_number || '';
      if (!toNumber) {
        const prompted = window.prompt('Enter Transfer Order (TO) number (leave blank to skip):', '');
        if (prompted !== null) toNumber = prompted.trim();
      }

      // Use passed components or self-fetch
      const resolvedComponents = (components && components.length > 0)
        ? components
        : await fetchComponents(order);

      // Fetch component descriptions (components table uses part_number, not sku)
      const skus = resolvedComponents.map(c => c.sku).filter(Boolean);
      const descMap: Record<string, string> = {};
      if (skus.length > 0) {
        const { data } = await supabase
          .from('components')
          .select('part_number, description')
          .in('part_number', skus);
        if (data) {
          data.forEach((c: { part_number: string; description: string }) => {
            descMap[c.part_number] = c.description;
          });
        }
      }

      // Fetch specs for MB and CAS components to derive remarks
      const remarkSkus = resolvedComponents
        .filter(c => ['MB', 'MOBO', 'MOTHERBOARD', 'CAS', 'CASE', 'CHASSIS'].includes((c.type || '').toUpperCase()))
        .map(c => c.sku)
        .filter(Boolean);
      const specsMap: Record<string, Record<string, any>> = {};
      if (remarkSkus.length > 0) {
        const { data: specsData } = await supabase
          .from('components')
          .select('part_number, specs')
          .in('part_number', remarkSkus);
        (specsData || []).forEach((c: any) => {
          if (c.specs && Object.keys(c.specs).length > 0) specsMap[c.part_number] = c.specs;
        });
      }
      const remarks = deriveRemarks(order.build?.sku || '', resolvedComponents, specsMap);

      // Report # = bom_revision (only bumps when BOM changes, not on every print)
      const reportNumber = order.bom_revision ?? 1;
      // Silently track print count
      await supabase
        .from('assembly_orders')
        .update({ print_count: (order.print_count ?? 0) + 1 })
        .eq('id', order.id);

      const html = buildPrintHTML(order, resolvedComponents, descMap, toNumber, canViewCosts, reportNumber, remarks);
      const win = window.open('', '_blank', 'width=900,height=750');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 600);
      }
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Button variant="outline" onClick={handlePrint} disabled={isPrinting}>
      <Printer className="h-4 w-4 mr-2" />
      {isPrinting ? 'Preparing...' : 'Print Build Sheet'}
    </Button>
  );
}
